import { Component, Input, OnInit } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { TabRole, TabsService } from 'src/app/libs/components/tabs-host/tabs.service';
import { ActivableStateTypeColors, ActivableStateTypes, DraftableStateTypes, EActivableStateType, EDraftableStateType, ProductStateTypeColors, TId } from 'src/app/models';
import { MockResource } from 'src/app/resources';
import { MasterApiService } from 'src/app/services/master-api.service';
import { ProductsEditComponent } from '../../../../products-operative-products/products-edit/products-edit.component';
import { MaintenancePlansEditComponent } from 'src/app/routes/assets/operative/maintenance-plans/maintenance-plans-edit/maintenance-plans-edit.component';

type ProcessSheetLink = {
  type: 'product' | 'maintenancePlan';
  id: TId;
  code: string;
  name: string;
  stateId: number;
}

@Component({
    selector: 'app-process-sheet-links',
    templateUrl: './process-sheet-links.component.html',
    styleUrl: './process-sheet-links.component.sass',
    standalone: false
})
export class ProcessSheetLinksComponent implements OnInit {

  @Input() processSheetId?: TId;

  links: ProcessSheetLink[] = [];
  linksResource?: MockResource<ProcessSheetLink>;
  ready: boolean = false;

  productStates = DraftableStateTypes.map(_ => ({ id: _.id, name: this.tr.instant(`enumeration.DraftableStateTypes.${_.name}`) }));
  maintenancePlanStates = ActivableStateTypes.map(_ => ({ id: _.id, name: this.tr.instant(`enumeration.ActivableStateTypes.${_.name}`) }));


  constructor(private api:MasterApiService, private tr:TranslateService, private tabs:TabsService) { }

  async ngOnInit(): Promise<void> {
    await this.buildLinks();
  }

  buildLinks = async () => {
    if (!this.processSheetId)
      return;

    const filter = { 'processSheetId': this.processSheetId };

    const products         = await firstValueFrom(this.api.resources.product.products.getAll(filter));
    const maintenancePlans = await firstValueFrom(this.api.resources.maintenancePlans.getAll(filter));

    this.links = [
      ...products.map(_ => ({
        type: 'product' as const,
        id: _.id,
        code: _.code,
        name: _.name,
        stateId: _.stateId,
      })),
      ...maintenancePlans.map(_ => ({
        type: 'maintenancePlan' as const,
        id: _.id,
        code: _.code,
        name: _.name,
        stateId: _.stateId,
      })),
    ];

    this.linksResource = new MockResource<ProcessSheetLink>('processSheetLinks', this.links);
    this.ready = true;
  }


  getTypeName = (link: ProcessSheetLink): string => {
    if (link.type === 'product')
      return this.tr.instant('entity.product.product');

    if (link.type === 'maintenancePlan')
      return this.tr.instant('entity.maintenancePlans.maintenancePlan');

    throw new Error('Unknown type');
  }

  getStateName = (link: ProcessSheetLink): string => {
    if (link.type === 'product')
      return this.productStates.find(s => s.id === link.stateId)?.name;

    if (link.type === 'maintenancePlan')
      return this.maintenancePlanStates.find(s => s.id === link.stateId)?.name;

    throw new Error('Unknown type');
  }

  getStateColor = (link: ProcessSheetLink): string => {
    if (link.type === 'product')
      return ProductStateTypeColors[link.stateId as EDraftableStateType] || '';

    if (link.type === 'maintenancePlan')
      return ActivableStateTypeColors[link.stateId as EActivableStateType] || '';

    throw new Error('Unknown type');
  }

  navigateToEditor = (link: ProcessSheetLink) => {
    if (link.type === 'product')
      this.tabs.createTab(ProductsEditComponent,          TabRole.EDITION, { inputPrms: { id: link.id }});

    if (link.type === 'maintenancePlan')
      this.tabs.createTab(MaintenancePlansEditComponent,  TabRole.EDITION, { inputPrms: { id: link.id }});
  }
}